import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import type { FileRejection } from "react-dropzone";

import { toastError } from "@alpacahq/ui/old";

import { useGetOwnerDocuments } from "~/v2/api/hooks";
import { deleteFileUpload } from "~/v2/api/rest/entityOnboarding";
import type { ExistingFile } from "~/v2/components/fileUpload";
import { normalizeDocType } from "~/v2/utils/normalizeDocType";

export interface UseUploadOwnerDocumentArgs {
  accountId: string;
  ownerId: string;
  docType: string;
}

/**
 * Deferred single-document upload for an owner. Generalizes the Photo ID
 * pattern (see useUploadID): the file is held in local state and uploaded only
 * after the owner exists, so it works when adding a brand-new owner (no
 * ownerId yet). An already-saved document is seeded from the owner's documents
 * and deleted server-side immediately on removal.
 */
export const useUploadOwnerDocument = ({
  accountId,
  ownerId,
  docType,
}: UseUploadOwnerDocumentArgs) => {
  const { documents, refetch } = useGetOwnerDocuments({
    accountId,
    ownerId,
    options: { enabled: !!ownerId },
  });

  const existingDoc = documents?.find(
    (doc) => normalizeDocType(doc.doc_type) === docType,
  );

  const [file, setFile] = useState<File | ExistingFile | null>(
    existingDoc ? { id: existingDoc.id, name: existingDoc.doc_type } : null,
  );
  const [error, setErrorState] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) =>
      deleteFileUpload({ accountId, ownerId, documentId }),
    onSuccess: () => {
      refetch();
    },
    onError: (err) => {
      console.error(err);
      toastError("Failed to delete document.");
    },
  });

  const onUpload = (uploaded: File | ExistingFile) => {
    setFile(uploaded);
    setErrorState(null);
  };

  const onError = (rejection: FileRejection) => {
    setFile(null);
    setErrorState(rejection.errors.map((err) => err.message).join(", "));
  };

  const onRemove = () => {
    // Delete server-side immediately only for an already-saved document (has an
    // id). A not-yet-saved File is just cleared from local state.
    if (file && "id" in file) {
      deleteMutation.mutate(file.id);
    }
    setFile(null);
    setErrorState(null);
  };

  const setError = (message: string | null) => {
    setErrorState(message);
  };

  // Re-seed when the owner (or their saved document) changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: accountId and ownerId are triggers: the file state is cleared when the owner being uploaded for changes. existingDoc?.id is deliberately narrower than existingDoc because this effect writes state and the fetched object gets a new identity on every settle, which would loop.
  useEffect(() => {
    setFile(
      existingDoc ? { id: existingDoc.id, name: existingDoc.doc_type } : null,
    );
    setErrorState(null);
  }, [accountId, ownerId, existingDoc?.id]);

  return { file, error, onUpload, onError, onRemove, setError };
};

export type UseUploadOwnerDocumentReturn = ReturnType<
  typeof useUploadOwnerDocument
>;
